import React, { useEffect, useState } from "react";
import Head from "src/layout/head/Head";
import {
  Block, BlockTitle, Icon
} from "src/components/Component";
import moment from "moment";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import CurrencyFormat from 'src/transformer/currency-format';
import { useSelector } from "react-redux";
import { convertPenniesToDecimals } from "src/utils/money";
import ScheduledPaymentStatusTransformer from "src/transformer/scheduled-payment-status";
import { Alert } from "reactstrap";
import { Spinner } from "reactstrap";
import { TrashIcon } from "@heroicons/react/24/outline";
import Axios from "axios";
import { API_ROUTES } from "src/config/api";
import { Auth } from "aws-amplify";
import { ArrowPathIcon, CheckCircleIcon, EnvelopeIcon } from "@heroicons/react/20/solid";

const BookingTransactions = ({ data, loading, triggerReload }) => {
  const { enums } = useSelector((state) => state.enums);
  const [completedItems, setCompletedItems] = useState([]);
  const [inCompletedItems, setInCompletedItems] = useState([]);
  const [refreshCount, setRefreshCount] = useState(0);
  const [itemDeleting, setItemDeleting] = useState(null);
  const [approvalLinkTriggered, setApprovalLinkTriggered] = useState(false);
  const [approvalLinkTriggering, setApprovalLinkTriggering] = useState(false);

  const deleteScheduledPayment = async (id) => {
    setItemDeleting(id);
    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.payment['scheduled-payment:delete'](id),
      {
        method: 'delete',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setItemDeleting(null);
    triggerReload();
  };

  useEffect(() => {
    let refreshTimeout = null;

    if (loading === false) {
      let comp = [];
      let inComp = [];
      let hasInProgress = false;

      for (const item of data.scheduled_transactions) {
        // If we have an in progress item, we need to refresh the data
        if (item.status.id === 'IN_PROGRESS' || item.status.id === 'PROCESSING') {
          hasInProgress = true;
        }

        // Group the items into completed and incompleted
        if (item.status.id === 'QUEUED' || item.status.id === 'IN_PROGRESS' || item.status.id === 'AWAITING_APPROVAL' || item.status.id === 'PROCESSING') {
          inComp.push(item);
        } else {
          comp.push(item);
        }
      }

      // If we have an in progress item, we need to refresh the data
      if (hasInProgress === true && refreshCount < 30) {
        refreshTimeout = setTimeout(() => {
          triggerReload();
          setRefreshCount(refreshCount + 1);
        }, 10000);
      }

      // Sort the items by created date
      comp.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });

      inComp.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setCompletedItems(comp);
      setInCompletedItems(inComp);
    }

    return () => {
      clearTimeout(refreshTimeout);
    }
  }, [loading]);

  const triggerApprovalLink = async (id) => {
    setApprovalLinkTriggering(true);

    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.agent['booking:approval-link'](id),
      {
        method: 'post',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setApprovalLinkTriggering(false);
    setApprovalLinkTriggered(true);
  };

  const titleClass = 'text-primary mb-0 text-xl font-bold';
  const descriptionClass = 'text-secondary text-sm';

  return (
    <React.Fragment>
      <Head title="Schedule Payments" />

      {inCompletedItems.filter((item) => item.status.id === 'IN_PROGRESS').length > 0 && (
        <Alert color="primary" className="alert-icon">
          {" "}
          <Icon name="info" className="!text-2xl !top-[21px] !left-[17px]" /> <strong>Scheduled payment(s) are currently being processed</strong><span className="text-sm block">Their status will update below shortly when complete</span>
        </Alert>
      )}

      <div className="mb-3">
        {inCompletedItems.filter((item) => item.status.id === 'AWAITING_APPROVAL').length > 0 && (
          <div className="float-right">
            <button
              className={`btn text-white ${approvalLinkTriggering === true ? 'disabled:bg-slate-600 bg-slate-600 disabled:opacity-100' : approvalLinkTriggered === true ? ' disabled:bg-emerald-500 bg-emerald-500 disabled:opacity-100' : 'bg-violet-900'} `}
              onClick={() => triggerApprovalLink(data.id)}
              disabled={approvalLinkTriggering === true || approvalLinkTriggered === true}
            >
                { approvalLinkTriggering === true && <ArrowPathIcon className="h-5 w-5 text-white mr-1 animate-spin	-mt-0.5" />}
                { approvalLinkTriggered === true && <CheckCircleIcon className="h-5 w-5 text-white mr-1 -mt-0.5" />}
                { approvalLinkTriggered === false && approvalLinkTriggering === false && <EnvelopeIcon className="h-5 w-5 text-white mr-1 -mt-0.5" /> }
                { approvalLinkTriggering === true ? 'Sending Approval Link to Customer' : approvalLinkTriggered === true ? 'Approval Link Sent to Customer' : 'Send Approval Link to Customer' }
            </button>
          </div>
        )}
        <BlockTitle tag="h4" className={titleClass}>Future Scheduled Payments</BlockTitle>
        <p className={descriptionClass}>Payments that are scheduled to be taken in the future</p>
      </div>

      <Block>
        <table className="table table-tranx border-light card-bordered">
          <thead>
            <tr className='tb-tnx-item'>
              <th scope="col" className="text-center">Amount</th>
              <th scope="col" className="text-center border-l">Will be Actioned On</th>
              <th scope="col" className="text-center border-l">Status</th>
              <th scope="col" className="text-center border-l">Created At</th>
              <th scope="col" className="text-center border-l pr-0 pl-0">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(loading === true) && [0, 1].map((item) => (
              <tr className='tb-tnx-item' key={item}>
                <td colSpan={6}>
                  <Skeleton count={1} height="40px" />
                </td>
              </tr>
            ))}
            {loading === false && inCompletedItems.length === 0 && (
              <tr className='tb-tnx-item'>
                <td colSpan={6} className="text-center">
                  <strong>No payments are scheduled</strong>
                </td>
              </tr>
            )}
            {loading === false && inCompletedItems.map((item, key) => {
              return (
                <tr className='tb-tnx-item' key={key}>
                  <td className="text-center">
                    {CurrencyFormat(enums.currency, item.currency).format(convertPenniesToDecimals(item.amount))}
                  </td>
                  <td className="text-center border-l">{moment(item.execute_after).format('Do MMMM YYYY, h:mm:ss a')}</td>
                  <td className="text-center border-l">
                    {(item.status.id === 'IN_PROGRESS' || item.status.id === 'PROCESSING') && (
                      <React.Fragment>
                        <Spinner size="sm" animation="border" role="status" className="mr-2 text-primary"> </Spinner>
                        Processing
                      </React.Fragment>
                    )}
                    {(item.status.id !== 'IN_PROGRESS' && item.status.id !== 'PROCESSING') && (
                      <ScheduledPaymentStatusTransformer status={item.status.id} />
                    )}
                  </td>
                  <td className="text-center border-l">{moment(item.created_at).format('Do MMMM YYYY, h:mm:ss a')}</td>
                  <td className="text-center border-l px-0">
                    {(item.status.id === 'QUEUED' || item.status.id === 'AWAITING_APPROVAL') && (
                      <React.Fragment>
                        {itemDeleting === item.id && (
                          <Spinner size="sm" animation="border" role="status" className="inline-block text-primary"> </Spinner>
                        )}
                        {itemDeleting !== item.id && (
                          <TrashIcon className="h-5 w-5 text-red-800 cursor-pointer inline-block" onClick={() => deleteScheduledPayment(item.id)} />
                        )}
                      </React.Fragment>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Block>

      <div className="mt-5 mb-3">
        <BlockTitle tag="h4" className={titleClass}>Past Scheduled Payments</BlockTitle>
        <p className={descriptionClass}>Scheduled payments that have been undertaken</p>
      </div>

      <Block>
        <table className="table table-tranx border-light card-bordered">
          <thead>
            <tr className='tb-tnx-item'>
              <th scope="col">Amount</th>
              <th scope="col" className="text-center border-l">Created At</th>
              <th scope="col" className="text-center border-l">Actioned On</th>
              <th scope="col" className="text-center border-l">Status</th>
              <th scope="col" className="text-center border-l pr-0 pl-0">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(loading === true) && [0, 1].map((item) => (
              <tr className='tb-tnx-item' key={item}>
                <td colSpan={6}>
                  <Skeleton count={1} height="40px" />
                </td>
              </tr>
            ))}
            {loading === false && completedItems.length === 0 && (
              <tr className='tb-tnx-item'>
                <td colSpan={6} className="text-center">
                  <strong>No payments found</strong>
                </td>
              </tr>
            )}
            {loading === false && completedItems.map((item, key) => {
              return (
                <tr className='tb-tnx-item' key={key}>
                  <td className="text-center border-r">
                    {CurrencyFormat(enums.currency, item.currency).format(convertPenniesToDecimals(item.amount))}
                  </td>
                  <td className="text-center">{moment(item.created_at).format('Do MMMM YYYY, h:mm:ss a')}</td>
                  <td className="text-center border-l">{moment(item.completed_at).format('Do MMMM YYYY, h:mm:ss a')}</td>
                  <td className="text-center border-l"><ScheduledPaymentStatusTransformer status={item.status.id} /></td>
                  <td className="border-l text-center pl-2 pr-2" style={{ width: '75px' }}>
                    <ul className="nk-tb-actions gx-1 center">
                      <li>
                        <Link
                          to={`/transaction/${item.transaction.id}`}
                          className="text-soft dropdown-toggle btn btn-icon btn-trigger"
                        >
                          <Icon name="info" />
                        </Link>
                      </li>
                    </ul>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Block>

    </React.Fragment>
  );
};
export default BookingTransactions;
